import { useEffect } from "react";
import { LOCAL_SEO_KEYWORDS } from "./LocalSeoPages";

const BASE_URL = "https://viscar.it";
const DEFAULT_OG_IMAGE = "https://customer-assets.emergentagent.com/job_soccorso-messina/artifacts/tbry4sru_1%20%2818%29.jpeg";

const ensureMetaTag = (selector, attributes, content) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value));
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const ensureLinkTag = (selector, rel, href) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
};

const upsertStructuredData = (id, payload) => {
  let tag = document.head.querySelector(`#${id}`);
  if (!tag) {
    tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(payload);
};

export const SeoManager = ({ pathname }) => {
  useEffect(() => {
    const normalizedPathname = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
    const seoMap = {
      "/": {
        title: "Viscar | Carroattrezzi e soccorso stradale H24 a Messina",
        description: "Carroattrezzi e soccorso stradale H24 a Messina per auto, furgoni, camion e mezzi pesanti. Intervento rapido da Tremestieri su Messina, A18 e A20.",
        keywords: [
          "soccorso stradale messina",
          "soccorso stradale provincia di messina",
          "carroattrezzi messina",
          "carro attrezzi messina",
          "carroattrezzi provincia di messina",
          "soccorso autostradale a18",
          "soccorso autostradale a20",
          "soccorso mezzi pesanti messina",
          "carroattrezzi milazzo",
          "carroattrezzi taormina",
        ],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/soccorso-h24": {
        title: "Soccorso stradale H24 e carroattrezzi a Messina | Viscar",
        description: "Soccorso stradale H24 Viscar per auto, furgoni, camion, mezzi pesanti, recovery, recuperi complessi e carroattrezzi a Messina, A18 e A20.",
        keywords: [
          "soccorso stradale messina",
          "carroattrezzi messina",
          "carro attrezzi messina",
          "soccorso stradale provincia di messina",
          "soccorso autostradale messina",
          "soccorso autostradale a18",
          "soccorso autostradale a20",
          "soccorso mezzi pesanti messina",
        ],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/viscar-soccorso-stradale-h24": {
        title: "Viscar soccorso stradale H24 | Home pubblicitaria",
        description: "Home pubblicitaria Viscar con accesso a carroattrezzi, soccorso stradale H24, landing locali e contatti diretti su Messina e provincia.",
        keywords: [
          "viscar soccorso stradale h24",
          "viscar messina",
          "carroattrezzi viscar",
          "soccorso stradale viscar",
        ],
        robots: "noindex,follow",
      },
      "/meccatronica": {
        title: "Meccatronica car & truck a Messina | Viscar",
        description: "Officina meccatronica Viscar a Messina per diagnosi, assistenza tecnica, manutenzione e supporto car & truck.",
        keywords: ["meccatronica messina", "officina meccatronica messina", "diagnosi elettronica messina"],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/gommista": {
        title: "Gommista car & truck a Messina | Viscar",
        description: "Servizio gomme Viscar a Messina per auto, furgoni, autocarri e mezzi da lavoro con supporto tecnico dedicato.",
        keywords: ["gommista messina", "gomme truck messina", "cambio gomme autocarro messina"],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/contatti": {
        title: "Contatti Viscar | Soccorso stradale H24 e officina a Messina",
        description: "Contatti diretti Viscar: soccorso stradale H24, officina, meccatronica, servizio gomme e sede a Larderia - Polo ASI, Messina.",
        keywords: ["contatti viscar", "telefono soccorso stradale messina", "carroattrezzi messina telefono"],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/carroattrezzi-messina": {
        title: "Carroattrezzi a Messina H24 | Viscar",
        description: "Carroattrezzi a Messina H24 per auto, furgoni e mezzi da lavoro. Intervento rapido da Tremestieri verso città, A18 e A20.",
        keywords: [...LOCAL_SEO_KEYWORDS.shared, ...LOCAL_SEO_KEYWORDS["carroattrezzi-messina"]],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/carroattrezzi-tremestieri": {
        title: "Carroattrezzi a Tremestieri | Viscar Messina",
        description: "Carroattrezzi a Tremestieri con accesso rapido alla viabilità principale, supporto H24 e collegamento operativo con Messina, A18 e A20.",
        keywords: [...LOCAL_SEO_KEYWORDS.shared, ...LOCAL_SEO_KEYWORDS["carroattrezzi-tremestieri"]],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/carroattrezzi-villafranca-tirrena": {
        title: "Carroattrezzi a Villafranca Tirrena | Viscar",
        description: "Carroattrezzi a Villafranca Tirrena con traino, recupero e supporto H24 collegato a Messina e ai principali accessi dell'area ovest.",
        keywords: [...LOCAL_SEO_KEYWORDS.shared, ...LOCAL_SEO_KEYWORDS["carroattrezzi-villafranca-tirrena"]],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/soccorso-stradale-messina": {
        title: "Soccorso stradale a Messina H24 | Viscar",
        description: "Soccorso stradale a Messina H24 per auto, furgoni e mezzi da lavoro con partenza rapida da Tremestieri, supporto locale e contatto immediato.",
        keywords: [...LOCAL_SEO_KEYWORDS.shared, ...LOCAL_SEO_KEYWORDS["soccorso-stradale-messina"]],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/soccorso-stradale-provincia-di-messina": {
        title: "Soccorso stradale in provincia di Messina | Viscar",
        description: "Soccorso stradale in provincia di Messina con operatività H24, collegamento con Messina e copertura dei principali snodi del territorio.",
        keywords: [...LOCAL_SEO_KEYWORDS.shared, ...LOCAL_SEO_KEYWORDS["soccorso-stradale-provincia-di-messina"]],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/soccorso-stradale-milazzo": {
        title: "Soccorso stradale a Milazzo | Viscar",
        description: "Soccorso stradale a Milazzo con accesso diretto a Viscar, supporto H24 e collegamento con Messina e provincia.",
        keywords: [...LOCAL_SEO_KEYWORDS.shared, ...LOCAL_SEO_KEYWORDS["soccorso-stradale-milazzo"]],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/soccorso-stradale-taormina": {
        title: "Soccorso stradale a Taormina | Viscar",
        description: "Soccorso stradale a Taormina con riferimento ad A18, collegamenti con Messina e accesso rapido al servizio H24.",
        keywords: [...LOCAL_SEO_KEYWORDS.shared, ...LOCAL_SEO_KEYWORDS["soccorso-stradale-taormina"]],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/soccorso-autostradale-a18-a20": {
        title: "Soccorso autostradale A18 A20 | Viscar Messina",
        description: "Soccorso autostradale su A18 e A20 con base a Messina. Viscar presidia ricerche locali ad alta urgenza con assistenza H24 e recovery.",
        keywords: [...LOCAL_SEO_KEYWORDS.shared, ...LOCAL_SEO_KEYWORDS["soccorso-autostradale-a18-a20"]],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/soccorso-mezzi-pesanti-messina": {
        title: "Soccorso mezzi pesanti a Messina | Viscar",
        description: "Soccorso mezzi pesanti a Messina per camion, autobus, motrici e semirimorchi con heavy recovery e supporto su A18 e A20.",
        keywords: [...LOCAL_SEO_KEYWORDS.shared, ...LOCAL_SEO_KEYWORDS["soccorso-mezzi-pesanti-messina"]],
        robots: "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
      },
      "/privacy-policy": {
        title: "Privacy Policy | Viscar",
        description: "Informativa privacy del sito Viscar con indicazioni su dati raccolti, finalità, strumenti e diritti dell’utente.",
        keywords: ["privacy viscar"],
        robots: "noindex,follow",
      },
      "/cookie-policy": {
        title: "Cookie Policy | Viscar",
        description: "Cookie Policy del sito Viscar con spiegazione dei cookie necessari, analytics e gestione delle preferenze.",
        keywords: ["cookie viscar"],
        robots: "noindex,follow",
      },
    };

    const hasKnownSeoEntry = Boolean(seoMap[normalizedPathname]);
    const current = seoMap[normalizedPathname] || {
      title: "Pagina non disponibile | Viscar",
      description: "Questa pagina non è disponibile oppure è stata sostituita da un percorso aggiornato del sito Viscar.",
      keywords: ["viscar messina"],
      robots: "noindex,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
    };
    const canonicalPath = normalizedPathname === "/" || normalizedPathname === "/soccorso-h24" ? "/" : (hasKnownSeoEntry ? normalizedPathname : "/");
    const canonicalUrl = `${BASE_URL}${canonicalPath}`;

    document.title = current.title;
    ensureMetaTag('meta[name="description"]', { name: "description" }, current.description);
    ensureMetaTag('meta[name="keywords"]', { name: "keywords" }, (current.keywords || []).join(", "));
    ensureMetaTag('meta[name="robots"]', { name: "robots" }, current.robots);
    ensureMetaTag('meta[name="author"]', { name: "author" }, "Viscar");
    ensureMetaTag('meta[name="geo.region"]', { name: "geo.region" }, "IT-ME");
    ensureMetaTag('meta[name="geo.placename"]', { name: "geo.placename" }, "Messina, Provincia di Messina, Sicilia");
    ensureMetaTag('meta[property="og:title"]', { property: "og:title" }, current.title);
    ensureMetaTag('meta[property="og:description"]', { property: "og:description" }, current.description);
    ensureMetaTag('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
    ensureMetaTag('meta[property="og:image"]', { property: "og:image" }, DEFAULT_OG_IMAGE);
    ensureMetaTag('meta[property="og:image:alt"]', { property: "og:image:alt" }, "Carroattrezzi e soccorso stradale Viscar a Messina");
    ensureMetaTag('meta[property="business:contact_data:locality"]', { property: "business:contact_data:locality" }, "Messina");
    ensureMetaTag('meta[property="business:contact_data:region"]', { property: "business:contact_data:region" }, "Sicilia");
    ensureMetaTag('meta[property="business:contact_data:country_name"]', { property: "business:contact_data:country_name" }, "Italia");
    ensureMetaTag('meta[name="twitter:title"]', { name: "twitter:title" }, current.title);
    ensureMetaTag('meta[name="twitter:description"]', { name: "twitter:description" }, current.description);
    ensureMetaTag('meta[name="twitter:image"]', { name: "twitter:image" }, DEFAULT_OG_IMAGE);
    ensureLinkTag('link[rel="canonical"]', "canonical", canonicalUrl);

    upsertStructuredData("viscar-organization-schema", {
      "@context": "https://schema.org",
      "@type": "AutomotiveBusiness",
      name: "Viscar",
      url: BASE_URL,
      telephone: "+39 3517523353",
      sameAs: [
        "https://www.facebook.com/viscarautoriparazioni",
        "https://www.instagram.com/viscar_car_e_truck",
        "https://www.tiktok.com/@viscar2018",
      ],
      areaServed: ["Messina", "Provincia di Messina", "Milazzo", "Taormina", "Tremestieri", "A18", "A20"],
      address: {
        "@type": "PostalAddress",
        addressLocality: "Messina",
        addressCountry: "IT",
        streetAddress: "Larderia – Polo ASI, Capannone 18",
      },
    });

    upsertStructuredData("viscar-website-schema", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Viscar",
      url: BASE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${BASE_URL}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });

    if (normalizedPathname === "/" || normalizedPathname === "/soccorso-h24") {
      upsertStructuredData("viscar-home-service-schema", {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Carroattrezzi e soccorso stradale H24 a Messina",
        serviceType: "Carroattrezzi, soccorso stradale H24, soccorso autostradale, heavy recovery, officina mobile",
        areaServed: ["Messina", "Provincia di Messina", "Milazzo", "Taormina", "Tremestieri", "A18", "A20"],
        provider: {
          "@type": "AutomotiveBusiness",
          name: "Viscar",
          url: BASE_URL,
          telephone: "+39 3517523353",
        },
        url: `${BASE_URL}/`,
        description: "Carroattrezzi e soccorso stradale H24 a Messina e provincia con recovery leggero e pesante, officina mobile e presidio locale su A18 e A20.",
      });

      upsertStructuredData("viscar-home-faq-schema", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Viscar fa carroattrezzi e soccorso stradale H24 a Messina?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Sì, Viscar presidia Messina con carroattrezzi, soccorso stradale H24, recovery leggero e pesante, officina mobile e supporto su A18 e A20.",
            },
          },
          {
            "@type": "Question",
            name: "Viscar copre anche Tremestieri, A18 e A20?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Sì, il sito comunica in modo esplicito la presenza operativa su Messina, Tremestieri e i collegamenti principali A18 e A20.",
            },
          },
          {
            "@type": "Question",
            name: "Perché ci sono pagine separate per carroattrezzi, soccorso stradale e mezzi pesanti?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Per intercettare ricerche locali diverse con contenuti più pertinenti, migliorando visibilità organica e qualità del traffico anche per campagne Ads.",
            },
          },
        ],
      });
    } else {
      document.head.querySelector("#viscar-home-service-schema")?.remove();
      document.head.querySelector("#viscar-home-faq-schema")?.remove();
    }
  }, [pathname]);

  return null;
};